/**
 * DataExportView
 * ─────────────────────────────────────────────────────────────────────────────
 * "Give me all my data" page. Vendors can download streamed CSV exports of
 * their activity log, stock ledger, and session history for any date range.
 *
 * The manifest is fetched from the server so adding new export types is
 * strictly a backend change.
 */

import React, { useCallback, useEffect, useState } from "react";
import {
  Container, Row, Col, Card, Form, Alert, Spinner, Badge,
} from "react-bootstrap";
import moment from "moment";
import {
  apiGetExportManifest,
  apiExportAuditActivityCsv,
  apiExportAuditStockCsv,
  apiExportAuditSessionsCsv,
  apiExportVendorDatasetCsv,
} from "../api/index";
import HelpTooltip from "../components/HelpTooltip";
import { AppButton } from "../components/ui";

const EXPORTERS = {
  activity: apiExportAuditActivityCsv,
  stock: apiExportAuditStockCsv,
  sessions: apiExportAuditSessionsCsv,
};

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function DataExportView() {
  const [manifest, setManifest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState({});
  const [period, setPeriod] = useState({
    from: moment().subtract(90, "days").format("YYYY-MM-DD"),
    to: moment().format("YYYY-MM-DD"),
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiGetExportManifest();
      setManifest(res?.data?.data || null);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to load manifest");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDownload = async (key, label, endpoint) => {
    // The three original datasets keep their named bindings; everything the
    // manifest adds is downloaded by the endpoint it names.
    const exporter = EXPORTERS[key] || (endpoint ? (params) => apiExportVendorDatasetCsv(endpoint, params) : null);
    if (!exporter) return;
    setDownloading((d) => ({ ...d, [key]: true }));
    setError(null);
    try {
      const res = await exporter({ from: period.from, to: period.to });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      downloadBlob(blob, `lusuku-${key}-${period.from}.csv`);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || `Failed to download ${label}`);
    } finally {
      setDownloading((d) => ({ ...d, [key]: false }));
    }
  };

  return (
    <Container fluid className="py-3">
      <Row>
        <Col>
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center flex-wrap">
                <div>
                  <Card.Title as="h4" className="mb-0">
                    <i className="fa fa-file-export mr-2" /> Data export
                    <HelpTooltip text="Download the raw data behind every chart, report, and ledger on your Lusuku account. Useful for tax filings, accountants, investors, and external auditors. Your data belongs to you." />
                  </Card.Title>
                  <div className="text-muted small mt-1">
                    Streamed CSVs. Nothing is deleted — exporting is always safe.
                  </div>
                </div>
                <div className="d-flex align-items-end" style={{ gap: 8 }}>
                  <div>
                    <Form.Label className="small mb-0">From</Form.Label>
                    <Form.Control
                      size="sm" type="date"
                      value={period.from}
                      onChange={(e) => setPeriod((p) => ({ ...p, from: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Form.Label className="small mb-0">To</Form.Label>
                    <Form.Control
                      size="sm" type="date"
                      value={period.to}
                      onChange={(e) => setPeriod((p) => ({ ...p, to: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              {error && <Alert variant="danger">{error}</Alert>}
              {loading && <div className="text-center py-3"><Spinner size="sm" animation="border" /> Loading…</div>}

              {manifest && (
                <Row>
                  {manifest.exports.map((ex) => (
                    <Col md={4} key={ex.key} className="mb-3">
                      <Card style={{ height: "100%", borderLeft: "4px solid #2d7a5f" }}>
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <h6 className="mb-0">{ex.label}</h6>
                            <Badge bg="secondary">{ex.format}</Badge>
                          </div>
                          <p className="text-muted small" style={{ minHeight: 40 }}>
                            {ex.description}
                          </p>
                          {ex.dated === false && (
                            <div className="text-muted small mb-2">Whole record, not limited to the period.</div>
                          )}
                          <AppButton
                            onClick={() => handleDownload(ex.key, ex.label, ex.endpoint)}
                            disabled={!!downloading[ex.key]}
                            style={{ width: "100%" }}
                          >
                            {downloading[ex.key] ? "Preparing..." : `Download ${ex.label}`}
                          </AppButton>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}

              {manifest?.hint && (
                <Alert variant="light" className="mb-0 small">
                  <i className="fa fa-info-circle mr-1" /> {manifest.hint}
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
